import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider } from "./contexts/AuthContext";
import HomePage from "./pages/HomePage";
import ProtectedRoute from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";
import "@/App.css";

// Lazy-loaded routes (code-split for faster initial load)
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const CaseStudiesPage = lazy(() => import("./pages/CaseStudiesPage"));
const NormanBakosCaseStudy = lazy(() => import("./pages/NormanBakosCaseStudy"));
const StraussCaseStudy = lazy(() => import("./pages/StraussCaseStudy"));
const VideoProductionPage = lazy(() => import("./pages/VideoProductionPage"));
const LeadFormPage = lazy(() => import("./pages/LeadFormPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CampaignBuilder = lazy(() => import("./pages/CampaignBuilder"));
const StoryboardBuilder = lazy(() => import("./pages/StoryboardBuilder"));
const WebsiteBriefBuilder = lazy(() => import("./pages/WebsiteBriefBuilder"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const ContentSystemBuilder = lazy(() => import("./pages/ContentSystemBuilder"));
const GapDetector = lazy(() => import("./pages/GapDetector"));
const StoryboardGenerator = lazy(() => import("./pages/StoryboardGenerator"));
const BrandTransform = lazy(() => import("./pages/BrandTransform"));

// Lazy chat widget — non-critical, deferred until idle
const ChatWidget = lazy(() => import("./components/ChatWidget"));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]" data-testid="route-loading">
    <div className="w-10 h-10 border-2 border-[#38B2AC] border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App min-h-screen bg-[#0A0A0A] text-white">
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/case-studies" element={<CaseStudiesPage />} />
              <Route path="/case-studies/norman-bakos" element={<NormanBakosCaseStudy />} />
              <Route path="/case-studies/strauss-co" element={<StraussCaseStudy />} />
              <Route path="/video-production" element={<VideoProductionPage />} />
              <Route path="/lead-form" element={<LeadFormPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/campaign-builder"
                element={
                  <ProtectedRoute>
                    <CampaignBuilder />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/storyboard-builder"
                element={
                  <ProtectedRoute>
                    <StoryboardBuilder />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/website-brief-builder"
                element={
                  <ProtectedRoute>
                    <WebsiteBriefBuilder />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/support"
                element={
                  <ProtectedRoute>
                    <SupportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/content-system"
                element={
                  <ProtectedRoute>
                    <ContentSystemBuilder />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gap-detector"
                element={
                  <ProtectedRoute>
                    <GapDetector />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/storyboard-generator"
                element={
                  <ProtectedRoute>
                    <StoryboardGenerator />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/brand-transform"
                element={
                  <ProtectedRoute>
                    <BrandTransform />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
          <Toaster position="top-right" />
          <Suspense fallback={null}>
            <ChatWidget />
          </Suspense>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
